import { Loop } from '../core/Loop';
import { Assets } from '../core/Assets';
import { AudioManager } from '../core/AudioManager';
import { DeskScene } from '../scene/DeskScene';
import type { InspectSelection } from '../scene/DeskScene';
import { UI } from '../ui/UI';
import { titleScreen, dayIntroScreen, dayEndScreen, crisisScreen, endingScreen, creditsScreen } from '../ui/Screens';
import { Economy, AUDIT_BONUS, PAY_PER_CORRECT } from './Economy';
import { InspectController } from './Inspect';
import { DAYS, TODAY } from './data/days';
import { ENCOUNTERS } from './data/encounters';
import type { DayRule, Decision, Encounter, EndingId, RunFlags } from './data/types';
import { clearRun, hasBeatenGame, loadRun, loadScores, markBeaten, saveRun, submitScore } from './SaveGame';

type State = 'boot' | 'title' | 'dayIntro' | 'shift' | 'crisis' | 'dayEnd' | 'ending' | 'credits';

export class Game {
  private readonly assets = new Assets();
  private readonly audio = new AudioManager();
  private readonly ui = new UI();
  private scene!: DeskScene;
  private loop!: Loop;
  private econ = new Economy();
  private flags: RunFlags = {};
  private inspect!: InspectController;

  private state: State = 'boot';
  private dayIndex = 0;
  private encounterIndex = 0;
  private order: string[] = [];
  private enc: Encounter | null = null;
  private introDone = false;
  private docsOpen = false;
  private decisionMade = false;
  private usedDemo = false;
  private crisisFired = false;
  private earnedToday = 0;
  private floor = 0;
  private stopCrisis: (() => void) | null = null;

  constructor(private readonly canvas: HTMLCanvasElement) {}

  start(): void {
    this.ui.showScreen('<div class="screen-inner"><div class="screen-h">GLOBAL INTERACTIVE HOLDINGS</div><p class="screen-p dim" id="load-line">Provisioning your desk…</p></div>');
    this.scene = new DeskScene(this.canvas, this.assets, {
      onFieldSelected: (sel) => this.handleSelection(sel),
      onCharacterClicked: (sel) => this.handleSelection(sel),
      onStamped: (decision) => this.judge(decision),
      onFolderOpened: () => this.onDocsOpen(),
      onWeighed: (actual) => this.onWeighed(actual),
      sfx: (id) => this.audio.play(id as Parameters<AudioManager['play']>[0]),
    });
    this.scene.folderOpenRequested = () => {
      if (!this.enc || this.docsOpen) return;
      // Impatient players can pop the folder to skip a choice-free intro.
      if (!this.introDone && !this.enc.choice && !this.enc.noStamp) {
        this.introDone = true;
        this.ui.hideDialogue();
      }
      if (this.introDone) this.scene.spawnDocs(this.enc);
    };
    this.loop = new Loop(
      (dt) => this.scene.update(dt),
      () => this.scene.render(),
    );
    this.loop.start();
    this.bindUi();
    this.exposeDiagnostics();
    void this.assets.loadAll((done, total) => {
      const el = document.querySelector('#load-line');
      if (el) el.textContent = `Provisioning your desk… ${done}/${total}`;
    }).then(() => {
      this.scene.initVisuals();
      if (!this.tryDevRoute()) this.showTitle();
    });
  }

  // ---------------------------------------------------------------- dev routes
  /**
   * Query-string shortcuts for testing, e.g. index.html?person=grandma or
   * index.html?day=2. Not linked from any UI; console-logs what it loaded.
   */
  private tryDevRoute(): boolean {
    const params = new URLSearchParams(window.location.search);
    const person = params.get('person');
    if (person) return this.devLoadPerson(person);
    const day = params.get('day');
    if (day !== null) return this.devLoadDayIntro(Number(day));
    return false;
  }

  /** Finds an encounter by exact id, "dN-<query>" id, or a loose id/name substring match. */
  private findEncounterByQuery(query: string): { enc: Encounter; dayIndex: number } | null {
    const q = query.trim().toLowerCase();
    if (!q) return null;
    for (let i = 0; i < DAYS.length; i++) {
      for (const id of DAYS[i].encounterIds) {
        if (id.toLowerCase() === q || id.toLowerCase() === `d${DAYS[i].day}-${q}`) {
          return { enc: ENCOUNTERS[id], dayIndex: i };
        }
      }
    }
    for (let i = 0; i < DAYS.length; i++) {
      for (const id of DAYS[i].encounterIds) {
        const enc = ENCOUNTERS[id];
        if (id.toLowerCase().includes(q) || enc.name.toLowerCase().includes(q)) {
          return { enc, dayIndex: i };
        }
      }
    }
    return null;
  }

  /** Jumps straight into the desk with one specific encounter loaded, skipping title/day-intro. */
  private devLoadPerson(query: string): boolean {
    const found = this.findEncounterByQuery(query);
    if (!found) {
      console.warn(`[dev] person=${query} matched no encounter`);
      return false;
    }
    const { enc, dayIndex } = found;
    console.info(`[dev] person=${query} -> ${enc.id} (day ${dayIndex + 1})`);
    this.econ = new Economy();
    this.flags = {};
    this.dayIndex = dayIndex;
    this.econ.startDay();
    this.earnedToday = 0;
    this.floor = this.rollFloor();
    this.crisisFired = true;
    this.inspect = new InspectController(TODAY[this.day.day]);
    this.audio.music(this.day.music as 'day1');
    this.ui.renderPolicy(this.day.date, [this.floorRule(), ...this.day.rules]);
    this.ui.setHudVisible(true);
    this.ui.hideScreen();
    this.order = [enc.id];
    this.encounterIndex = 0;
    this.nextEncounter();
    return true;
  }

  /** Jumps to the between-day memo/interstitial screen for the given day number (1-based). Overwrites any local save. */
  private devLoadDayIntro(dayNumber: number): boolean {
    const idx = dayNumber - 1;
    if (!Number.isInteger(idx) || idx < 0 || idx >= DAYS.length) {
      console.warn(`[dev] day=${dayNumber} out of range (1-${DAYS.length})`);
      return false;
    }
    console.info(`[dev] day=${dayNumber} -> day-intro transition (local save will be overwritten)`);
    this.econ = new Economy();
    this.flags = {};
    this.dayIndex = idx;
    this.startDay();
    return true;
  }

  private bindUi(): void {
    window.addEventListener('pointerdown', () => {
      void this.audio.unlock();
    });

    this.ui.btnLogs.addEventListener('click', () => this.pullLogs());
    this.ui.btnDemo.addEventListener('click', () => this.demandDemo());
    this.ui.btnAudit.addEventListener('click', () => this.flagAudit());
    this.ui.btnMute.addEventListener('click', () => {
      this.audio.setMuted(!this.audio.muted);
      this.ui.btnMute.classList.toggle('muted', this.audio.muted);
    });
    this.ui.policyTab.addEventListener('click', () => {
      this.audio.play('click', 'ui');
      this.ui.togglePolicy();
    });
    this.ui.onDomInspect = (sel) => this.handleSelection(sel);

    window.addEventListener('keydown', (e) => {
      if (e.repeat) return;
      if (e.key === ' ' || e.key === 'Enter') {
        const next = document.querySelector<HTMLButtonElement>('#dialogue-next');
        if (next && !this.ui.dialogue.classList.contains('hidden') && !next.classList.contains('hidden')) {
          e.preventDefault();
          next.click();
        }
      } else if (e.key.toLowerCase() === 'p' && this.state === 'shift') {
        this.ui.togglePolicy();
      } else if (e.key.toLowerCase() === 'm') {
        this.ui.btnMute.click();
      }
    });
  }

  // ---------------------------------------------------------------- title/day flow
  private showTitle(): void {
    this.state = 'title';
    this.audio.music('title');
    this.ui.setHudVisible(false);
    const save = loadRun();
    const best = loadScores()[0] ?? null;
    titleScreen(this.ui, !!save, best, hasBeatenGame(),
      () => {
        clearRun();
        this.econ = new Economy();
        this.flags = {};
        this.dayIndex = 0;
        this.startDay();
      },
      () => {
        if (save) {
          this.econ = new Economy(save.newGamePlus);
          this.econ.credits = save.credits;
          this.econ.alloc = save.alloc;
          this.econ.stats = save.stats;
          this.flags = save.flags;
          this.dayIndex = save.day;
        }
        this.startDay();
      },
      () => {
        clearRun();
        this.econ = new Economy(true);
        this.flags = {};
        this.dayIndex = 0;
        this.startDay();
      });
  }

  private get day(): (typeof DAYS)[number] {
    return DAYS[this.dayIndex];
  }

  private startDay(): void {
    this.state = 'dayIntro';
    this.audio.play('memo');
    this.econ.startDay();
    this.earnedToday = 0;
    this.encounterIndex = 0;
    this.order = this.buildOrder();
    this.floor = this.rollFloor();
    this.crisisFired = false;
    this.inspect = new InspectController(TODAY[this.day.day]);
    saveRun({
      day: this.dayIndex,
      credits: this.econ.credits,
      alloc: this.econ.alloc,
      stats: this.econ.stats,
      flags: this.flags,
      newGamePlus: this.econ.scoreMultiplier > 1,
    });
    dayIntroScreen(this.ui, this.day, this.floor, () => {
      this.ui.hideScreen();
      this.audio.music(this.day.music as 'day1');
      this.ui.renderPolicy(this.day.date, [this.floorRule(), ...this.day.rules]);
      this.ui.setHudVisible(true);
      this.ui.togglePolicy(this.dayIndex === 0);
      this.nextEncounter();
    });
  }

  /** Tonight's profitability floor, rolled fresh so the KPI feels arbitrary. 0 = no floor this day. */
  private rollFloor(): number {
    const band = this.day.floorBand;
    if (!band) return 0;
    return band.min + Math.floor(Math.random() * (band.max - band.min + 1));
  }

  /** The headline Board rule for the day: clear the floor or be sunset. */
  private floorRule(): DayRule {
    return {
      id: 'r-floor',
      text: `PROFITABILITY FLOOR: ${this.floor}%. Under it, sunset. Players and reviews are not KPIs.`,
      newToday: true,
    };
  }

  /** Runtime verdict. Profit-class titles are judged by the floor; everything else keeps its authored intent. */
  private correctFor(enc: Encounter): Decision {
    if (enc.profitMargin !== undefined && !enc.auditExpected) {
      const clears = enc.profitMargin >= this.floor;
      return enc.correct === 'greenlit' && clears ? 'greenlit' : 'sunset';
    }
    return enc.correct;
  }

  /** Prepend the live margin-vs-floor row so the player can read the KPI they are judging. */
  private logsFor(enc: Encounter): typeof enc.logs {
    if (enc.profitMargin === undefined || enc.auditExpected) return enc.logs;
    const row = {
      label: 'OPERATING MARGIN (TTM)',
      value: `${enc.profitMargin}% (FLOOR ${this.floor}%)`,
      tag: 'margin',
      bad: enc.profitMargin < this.floor,
    };
    return [row, ...(enc.logs ?? [])];
  }

  /**
   * Draw a fresh set of applicants for the day, in random order.
   * Only `presentCount` of the pool are shown, so replays vary; anchored ids
   * keep their slot and `required` story beats are always drawn.
   */
  private buildOrder(): string[] {
    const day = this.day;
    const anchors = day.anchors ?? {};
    const required = day.required ?? [];
    const target = day.presentCount ?? day.encounterIds.length;

    const firsts = day.encounterIds.filter((id) => anchors[id] === 'first');
    const lasts = day.encounterIds.filter((id) => anchors[id] === 'last');
    const fixed = new Set<string>([...firsts, ...lasts]);

    // Required story beats that are not already anchored: always included, position shuffled.
    const reqMiddle = required.filter((id) => !fixed.has(id));
    for (const id of reqMiddle) fixed.add(id);

    // Random fill from whatever is left in the pool.
    const pool = day.encounterIds.filter((id) => !fixed.has(id));
    this.shuffle(pool);
    const need = Math.max(0, target - (firsts.length + lasts.length + reqMiddle.length));

    const middle = [...reqMiddle, ...pool.slice(0, need)];
    this.shuffle(middle);
    return [...firsts, ...middle, ...lasts];
  }

  private shuffle(arr: string[]): void {
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
  }

  private nextEncounter(): void {
    this.ui.clearFindings();
    this.ui.analytics.classList.add('hidden');
    this.scene.clearEncounter();
    this.inspect.reset();

    if (this.day.crisisAfter !== undefined && this.encounterIndex === this.day.crisisAfter && !this.crisisFired) {
      this.crisisFired = true;
      this.startCrisis();
      return;
    }
    if (this.encounterIndex >= this.order.length) {
      this.endDay();
      return;
    }
    this.state = 'shift';
    const enc = ENCOUNTERS[this.order[this.encounterIndex]];
    this.enc = enc;
    this.introDone = false;
    this.docsOpen = false;
    this.decisionMade = false;
    this.usedDemo = false;
    this.updateHud();
    this.updateTools();
    this.scene.presentEncounter(enc);
    window.setTimeout(() => {
      this.ui.say(enc.name.toUpperCase(), enc.intro, () => this.afterIntro());
    }, 500);
  }

  private afterIntro(): void {
    const enc = this.enc;
    if (!enc) return;
    if (enc.choice) {
      this.ui.showChoice(enc.choice.prompt, enc.choice.options, (opt) => {
        if (opt.sets) (this.flags as Record<string, boolean>)[opt.sets] = true;
        if (opt.credits) {
          this.econ.earn(opt.credits);
          this.econ.stats.bribesTaken += 1;
          this.updateHud();
        }
        if (opt.toast) this.ui.toast(opt.toast, 'gold');
        this.audio.play('click', 'ui');
        this.ui.say(enc.name.toUpperCase(), [opt.reply ?? '...'], () => this.afterChoice());
      });
    } else {
      this.afterChoice();
    }
  }

  private afterChoice(): void {
    const enc = this.enc;
    if (!enc) return;
    this.introDone = true;
    if (enc.noStamp) {
      this.ui.hideDialogue();
      this.scene.returnDocs(() => {
        this.encounterIndex += 1;
        this.nextEncounter();
      });
      return;
    }
    this.ui.say('DESK 7', ['Their folder is on the counter. Click it to spread the documents.'], () => {
      this.ui.hideDialogue();
    });
    this.updateTools();
  }

  private onDocsOpen(): void {
    this.docsOpen = true;
    this.ui.hideDialogue();
    this.updateTools();
  }

  // ---------------------------------------------------------------- inspection
  private handleSelection(sel: InspectSelection): void {
    const verdict = this.inspect.select(sel);
    const cls = verdict.kind === 'mismatch' ? 'mismatch' : verdict.kind === 'match' ? 'match' : '';
    this.ui.showCompare(
      `<strong>${verdict.text}</strong>${verdict.detail ? `<div class="cmp-vals">${verdict.detail}</div>` : ''}`,
      cls as 'mismatch',
      sel.clientX,
      sel.clientY,
    );
    if (verdict.kind === 'mismatch') {
      this.audio.play('denied', 'ui', 0.5);
      if (verdict.finding && !this.ui.hasFinding(verdict.finding)) {
        this.ui.addFinding(verdict.finding);
        this.econ.stats.discrepanciesFound += 1;
      }
    } else if (verdict.kind === 'match' || verdict.kind === 'info') {
      this.audio.play('click', 'ui', 0.6);
    }
  }

  private onWeighed(actual: number): void {
    const enc = this.enc;
    if (!enc?.coinWeight) return;
    const { claimed } = enc.coinWeight;
    this.ui.addFinding(`Scale: bag weighs in at ${actual.toLocaleString('en-US')} CR`, true);
    if (Math.abs(claimed - actual) / Math.max(claimed, actual) > 0.15) {
      const finding = `Declared ${claimed.toLocaleString('en-US')} CR ≠ measured ${actual.toLocaleString('en-US')} CR`;
      if (!this.ui.hasFinding(finding)) {
        this.ui.addFinding(finding);
        this.econ.stats.discrepanciesFound += 1;
        this.audio.play('denied', 'ui', 0.5);
      }
    }
  }

  // ---------------------------------------------------------------- tools
  private pullLogs(): void {
    const enc = this.enc;
    if (!enc || !this.docsOpen || this.decisionMade) return;
    if (this.ui.analyticsOpen) {
      this.ui.hideAnalytics();
      return;
    }
    this.audio.play('tally', 'ui', 0.7);
    this.ui.showAnalytics(enc.name.toUpperCase(), this.logsFor(enc));
  }

  private demandDemo(): void {
    const enc = this.enc;
    if (!enc || !this.docsOpen || this.decisionMade || this.usedDemo || enc.isCustomer) return;
    this.usedDemo = true;
    this.audio.play('alert', 'ui', 0.5);
    const text = enc.demo ?? '"A live demo? It\'s, uh. Not with me. It\'s rendering. Somewhere."';
    this.ui.say('LIVE DEMO', [text], () => this.ui.hideDialogue());
    if (enc.demoFinding && !this.ui.hasFinding(enc.demoFinding)) {
      this.ui.addFinding(enc.demoFinding);
      this.econ.stats.discrepanciesFound += 1;
    }
    this.updateTools();
  }

  private flagAudit(): void {
    const enc = this.enc;
    if (!enc || !this.docsOpen || this.decisionMade) return;
    if (enc.auditExpected) {
      this.decisionMade = true;
      this.audio.play('audit');
      this.scene.crisisShake();
      this.econ.stats.processed += 1;
      this.econ.stats.correctCount += 1;
      this.econ.stats.auditsCorrect += 1;
      this.econ.stats.sunsetCount += 1;
      const pay = PAY_PER_CORRECT + AUDIT_BONUS;
      this.econ.earn(pay);
      this.earnedToday += pay;
      this.ui.toast(`FRAUD CONFIRMED · LEGAL DISPATCHED · +${pay} CR`, 'gold');
      this.updateHud();
      this.ui.say('LEGAL', ['Two associates in identical gray suits appear without sound. The applicant is escorted somewhere that is not discussed.'], () => {
        this.ui.hideDialogue();
        this.scene.returnDocs(() => {
          this.encounterIndex += 1;
          this.nextEncounter();
        });
      });
    } else {
      this.econ.stats.auditsWrong += 1;
      const penalty = this.econ.recordMistake();
      this.audio.play('denied');
      this.updateHud();
      this.ui.showMemo(
        `Desk 7:\n\nYou summoned Legal on a legitimate applicant. Legal bills by the minute.\n\n${penalty > 0 ? `A ${penalty} CR deduction has been applied.` : 'This warning is complimentary. Further warnings will not be.'}\n\nThe applicant is still waiting. Process them.`,
        () => {
          this.checkMistakeFiring();
        },
      );
    }
    this.updateTools();
  }

  private updateTools(): void {
    const active = this.state === 'shift' && !!this.enc && !this.enc.noStamp;
    this.ui.setToolsEnabled({
      logs: active && this.docsOpen && !this.decisionMade,
      demo: active && this.docsOpen && !this.usedDemo && !this.decisionMade && !this.enc?.isCustomer,
      audit: active && this.docsOpen && !this.decisionMade,
    });
  }

  // ---------------------------------------------------------------- judgement
  private judge(decision: Decision): void {
    const enc = this.enc;
    if (!enc) return;
    this.decisionMade = true;
    this.updateTools();
    this.econ.stats.processed += 1;
    if (decision === 'greenlit') this.econ.stats.greenlitCount += 1;
    else this.econ.stats.sunsetCount += 1;

    if (enc.special === 'underground-plant' && decision === 'greenlit') {
      this.flags.undergroundHelped = true;
    }

    const want = this.correctFor(enc);
    const correct = decision === want;
    const reaction = decision === 'greenlit' ? enc.reactGreenlit : enc.reactSunset;

    const proceed = (): void => {
      this.ui.say(enc.name.toUpperCase(), [reaction], () => {
        this.ui.hideDialogue();
        this.scene.returnDocs(() => {
          this.encounterIndex += 1;
          this.nextEncounter();
        });
      });
    };

    if (correct) {
      this.econ.stats.correctCount += 1;
      this.econ.earn(PAY_PER_CORRECT);
      this.earnedToday += PAY_PER_CORRECT;
      this.audio.play('credit');
      this.ui.toast(`PROCESSED CORRECTLY · +${PAY_PER_CORRECT} CR`, 'good');
      this.updateHud();
      window.setTimeout(proceed, 350);
    } else {
      const penalty = this.econ.recordMistake();
      this.audio.play('memo');
      this.updateHud();
      const profitClass = enc.profitMargin !== undefined && !enc.auditExpected;
      let reason: string;
      if (profitClass) {
        reason = want === 'sunset'
          ? `The title posts a ${enc.profitMargin}% operating margin against tonight's floor of ${this.floor}%. It is under the floor. A license under the floor is a liability, whatever its players think. It should have been SUNSET.`
          : `The title clears the floor at ${enc.profitMargin}% against ${this.floor}%. You sunset a profitable license. That is revenue walking out the door, and the Board counts every credit.`;
      } else {
        reason = want === 'sunset'
          ? `Application should have been DENIED:\n· ${enc.violations.join('\n· ')}`
          : 'The application was clean and there was money in it. Denial without cause damages partner revenue.';
      }
      const memoText = `Desk 7:\n\nProcessing error on applicant "${enc.name}".\n\n${reason}\n\n${penalty > 0 ? `A ${penalty} CR deduction has been applied to your ledger.` : `This warning is complimentary (${this.econ.mistakesToday}/${this.econ.freeMistakesToday} today).`}\n\nThe Board reminds you that the bottom line is the only line.`;
      window.setTimeout(() => {
        this.ui.showMemo(memoText, () => {
          if (!this.checkMistakeFiring()) proceed();
        });
      }, 450);
    }
  }

  private checkMistakeFiring(): boolean {
    if (this.econ.firedForMistakes) {
      this.gotoEnding('fired');
      return true;
    }
    return false;
  }

  // ---------------------------------------------------------------- crisis
  private startCrisis(): void {
    this.state = 'crisis';
    this.audio.play('crisis');
    this.audio.play('alert');
    this.ui.setHudVisible(false);
    this.ui.hideDialogue();
    this.stopCrisis = crisisScreen(this.ui, this.econ.scoreMultiplier > 1, (_survived, meter) => {
      this.stopCrisis = null;
      this.flags.crisisSurvived = true;
      this.flags.crisisScore = Math.round(meter);
      this.ui.hideScreen();
      this.state = 'shift';
      this.ui.setHudVisible(true);
      if (meter > 55) {
        this.econ.earn(10);
        this.earnedToday += 10;
        this.ui.toast('PR FIRE CONTAINED · +10 CR retention bonus', 'gold');
      } else {
        this.ui.toast('PR fire contained. Barely. The Board noticed.', 'bad');
      }
      this.updateHud();
      this.nextEncounter();
    });
  }

  // ---------------------------------------------------------------- day end / endings
  private endDay(): void {
    this.state = 'dayEnd';
    this.audio.play('dayComplete');
    this.ui.setHudVisible(false);
    this.ui.hideDialogue();
    dayEndScreen(this.ui, this.day, this.econ, this.earnedToday, (fired) => {
      if (fired) {
        this.gotoEnding('fired');
        return;
      }
      if (this.dayIndex >= DAYS.length - 1) {
        this.gotoEnding(this.flags.leakTaken ? 'whistleblower' : 'loyal');
      } else {
        this.dayIndex += 1;
        this.startDay();
      }
    });
  }

  private gotoEnding(ending: EndingId): void {
    this.state = 'ending';
    this.stopCrisis?.();
    this.stopCrisis = null;
    this.ui.setHudVisible(false);
    this.ui.hideDialogue();
    clearRun();
    if (ending !== 'fired') markBeaten();
    if (ending === 'fired') {
      this.audio.play('fired');
      this.audio.music('endingFired');
    } else if (ending === 'whistleblower') {
      this.audio.music('endingWhistle');
    } else {
      this.audio.music('endingFired');
    }
    const score = Math.max(0, this.econ.legacyScore());
    endingScreen(this.ui, ending, this.flags, score, () => this.showCredits(ending, score));
  }

  private showCredits(ending: EndingId, score: number): void {
    this.state = 'credits';
    this.audio.music('credits');
    this.audio.play('tally');
    const endingName = ending === 'whistleblower' ? 'Whistleblower' : ending === 'loyal' ? 'Loyal Executive' : 'Terminated';
    const board = submitScore({ score, ending: endingName, when: new Date().toISOString().slice(0, 10) });
    creditsScreen(this.ui, this.econ.stats, this.flags, ending, score, board, () => {
      window.location.reload();
    });
  }

  // ---------------------------------------------------------------- misc
  private updateHud(): void {
    this.ui.updateHud(
      this.day.day,
      this.day.date,
      this.order.length - this.encounterIndex,
      this.econ.credits,
      this.econ.mistakesToday,
      this.econ.freeMistakesToday,
    );
  }

  private exposeDiagnostics(): void {
    Object.defineProperty(window, '__THREE_GAME_DIAGNOSTICS__', {
      configurable: true,
      get: () => ({
        state: this.state,
        day: this.day?.day,
        floor: this.floor,
        encounter: this.enc?.id ?? null,
        correct: this.enc ? this.correctFor(this.enc) : null,
        profitMargin: this.enc?.profitMargin ?? null,
        noStamp: this.enc?.noStamp ?? false,
        docsOpen: this.docsOpen,
        decisionMade: this.decisionMade,
        encounterIndex: this.encounterIndex,
        credits: this.econ.credits,
        mistakes: this.econ.mistakesToday,
        stats: this.econ.stats,
        flags: this.flags,
        renderer: this.scene?.diagnostics() ?? null,
      }),
    });
  }

  dispose(): void {
    this.loop.stop();
    this.stopCrisis?.();
    this.scene.dispose();
    this.assets.dispose();
  }
}
